import {
  ActionDefinitionFragment,
  ActionDefinitionHierarchyPreviewFragment,
  ActionDefinitionPreviewFragment,
  DeterministicToolInvocation as DeterministicToolInvocationFragment,
  DeterministicToolInvocations as DeterministicToolInvocationsFragment,
} from '@shared/generated/graphql';
import {
  ActionDefinition,
  ActionDefinitionChildLink,
  ActionDefinitionHierarchyPreview,
  ActionDefinitionPreview,
  CanonicalTool,
  DeterministicToolInvocations,
} from '@shared/types/action-definition';
import { isNil, omit } from 'lodash';
import {
  actionTriggerFromFragment,
  actionTriggerPreviewFromFragment,
} from './actionTrigger';
import { knowledgeBaseFromFragment } from './knowledgeBase';
import { workerPreviewFromFragment } from './worker';

export function actionDefinitionFromFragment(
  fragment: ActionDefinitionFragment
): ActionDefinition {
  if (isNil(fragment.procedure)) throw new Error('Procedure is required');
  return {
    ...actionDefinitionPreviewFromFragment(fragment),
    organizationId: fragment.organization.id,
    procedure: fragment.procedure,
    canonicalTools: fragment.canonicalTools ?? [],
    deterministicToolInvocations: deterministicToolInvocationsFromFragment(
      fragment.deterministicToolInvocations
    ),
    triggers: fragment.triggers?.map(actionTriggerPreviewFromFragment) ?? [],
    documents: fragment.documents
      ? fragment.documents.map((doc) => ({
          id: doc.id,
          name: doc.name ?? '',
          url: doc.url,
        }))
      : [],
    knowledgeBases:
      fragment.knowledgeBases?.map(knowledgeBaseFromFragment) ?? [],
    workersConfig: fragment.workersConfig,
    workers: fragment.workers?.map(workerPreviewFromFragment) ?? [],
    modelOverride: fragment.modelOverride ?? undefined,
    reasoningEffort: fragment.reasoningEffort ?? undefined,
    hasMemory: fragment.hasMemory ?? false,
    classifyConversationMessages:
      fragment.classifyConversationMessages ?? false,
    respondsToCoworkers: fragment.respondsToCoworkers ?? true,
    runDetached: fragment.runDetached ?? undefined,
    defaultAutonomyState: fragment.defaultAutonomyState ?? undefined,
    postCompletionMessageBehavior:
      fragment.postCompletionMessageBehavior ?? undefined,
    subscribers:
      (fragment.subscribers && fragment.subscribers.map((subs) => subs.id)) ??
      [],
    memoryEntityLinks:
      fragment.memoryEntityLinks?.map((link) => ({
        id: link.id,
      })) ?? [],
    globalPrompt: fragment.globalPrompt
      ? {
          id: fragment.globalPrompt.id,
          name: fragment.globalPrompt.name ?? null,
          prompt: fragment.globalPrompt.prompt ?? null,
          parameters: fragment.globalPrompt.parameters ?? null,
          basePromptId: fragment.globalPrompt.basePromptId ?? null,
        }
      : null,
    globalPromptSkillIds: fragment.globalPromptSkillIds ?? {},
    globalPromptValues: fragment.globalPromptValues ?? null,
    globalPromptToolValues: fragment.globalPromptToolValues ?? null,
    teamGlobalPromptValues:
      fragment.teamGlobalPromptValues?.map((override) => ({
        id: override.id,
        teamId: override.teamId,
        teamName: override.team.name ?? null,
        values: override.values ?? null,
        toolValues: override.toolValues ?? null,
      })) ?? [],
    provisioningResourceId: fragment.provisioningResourceId ?? null,
    relatedGlobalPrompts:
      fragment.relatedGlobalPrompts?.map((gp) => ({
        id: gp.id,
        name: gp.name ?? null,
      })) ?? [],
  };
}

export function actionDefinitionPreviewFromFragment(
  fragment: ActionDefinitionPreviewFragment
): ActionDefinitionPreview {
  return {
    id: fragment.id,
    createdAt: new Date(fragment.createdAt),
    updatedAt: fragment.updatedAt ? new Date(fragment.updatedAt) : null,
    name: createName(fragment.name, fragment.deletedAt),
    procedureType: fragment.procedure.procedureType,
    modality:
      'modality' in fragment.procedure
        ? fragment.procedure.modality
        : undefined,
    mode: fragment.mode,
    runDetached: fragment.runDetached ?? undefined,
    referringActionDefinitionsCount:
      fragment.referringActionDefinitionsCount ?? undefined,
    isEmbeddedInGraph: fragment.isEmbeddedInGraph ?? undefined,
    triggersCount: fragment.triggersCount ?? undefined,
    implementsTool: fragment.implementsTool ?? undefined,
    tags: fragment.tags ?? null,
    trackStepKind: fragment.sopMetadata?.trackStepKind ?? null,
    catalogKey: fragment.sopMetadata?.catalogKey ?? null,
  };
}

export function actionDefinitionHierarchyPreviewFromFragment(
  fragment: ActionDefinitionHierarchyPreviewFragment
): ActionDefinitionHierarchyPreview {
  return {
    ...actionDefinitionPreviewFromFragment(fragment),
    childLinks: fragment.childLinks.map(childLinkFromFragment),
    triggers: fragment.triggers?.map(actionTriggerFromFragment) ?? undefined,
  };
}

function childLinkFromFragment(
  link: ActionDefinitionHierarchyPreviewFragment['childLinks'][number]
): ActionDefinitionChildLink {
  const { actionDefinition } = link;
  return {
    relationshipType: link.relationshipType,
    actionDefinition: {
      id: actionDefinition.id,
      name: actionDefinition.name,
      procedureType: actionDefinition.procedure.procedureType,
      modality:
        'modality' in actionDefinition.procedure
          ? actionDefinition.procedure.modality
          : undefined,
      mode: actionDefinition.mode,
      isEmbeddedInGraph: actionDefinition.isEmbeddedInGraph ?? undefined,
      tags: actionDefinition.tags ?? null,
      trackStepKind: actionDefinition.sopMetadata?.trackStepKind ?? null,
    },
  };
}

export function createName(name: string, deletedAt?: Date): string {
  if (isNil(deletedAt)) return name;
  return `${name} (Deleted)`;
}

function deterministicToolInvocationsFromFragment(
  fragment?: DeterministicToolInvocationsFragment | null
): DeterministicToolInvocations | undefined {
  if (!fragment) return undefined;
  const onEnter = fragment.onEnter?.map(
    deterministicToolInvocationFromFragment
  );
  return { onEnter: onEnter ?? [] };
}

function deterministicToolInvocationFromFragment(
  t: DeterministicToolInvocationFragment
) {
  const tool = omit(t.tool, '__typename') as CanonicalTool;
  const input = t.input ?? {};
  return { tool, input };
}
